// Updated to use enhanced communication service
import { communicationService } from "@/app/_module/services/communicationService";
import type {
  CheckAreaNumberAvailableParams as EnhancedCheckParams,
  StartAreaNumberCheckoutParams as EnhancedCheckoutParams,
  SubscribeToAreaCodesParams as EnhancedSubscribeParams,
} from "@/app/_module/services/communicationService";

import type { 
  CheckAreaNumberAvailableParams,
  CheckAreaNumberAvailableResponseData,
  StartAreaNumberCheckoutParams,
  StartAreaNumberCheckoutResponse,
  AreaCodeCostResponse,
  SubscribeToAreaCodesParams,
  SubscribeToAreaCodesResponse
} from "../types/area.-code";

// Type adapters for backward compatibility
const adaptCheckParams = (params: CheckAreaNumberAvailableParams): EnhancedCheckParams => ({
  // Normalize to string[] to satisfy EnhancedCheckParams (number[] | string[])
  areaCodes: params.areaCodes.map((c) => String(c)),
});

const adaptCheckoutParams = (params: StartAreaNumberCheckoutParams): EnhancedCheckoutParams => ({
  // Backend Enhanced expects number[]; coerce from strings if needed
  areaCodes: params.areaCodes.map((c) => Number(c)),
  state: params.state,
  amount: params.amount,
  quantity: params.quantity,
  provider: params.provider,
  currency: params.currency,
});

const adaptSubscribeParams = (params: SubscribeToAreaCodesParams): EnhancedSubscribeParams => ({
  areaCodes: params.areaCodes,
  state: params.state,
  transactionId: params.transactionId,
});

export const getAreaCodes = async (): Promise<Record<string, number[]>> => {
  try {
    return await communicationService.getAreaCodes();
  } catch (error) {
    // Enhanced service already handles errors and toasts
    throw error;
  }
};

// Check area number availability
export const checkAreaNumberAvailable = async (params: CheckAreaNumberAvailableParams): Promise<CheckAreaNumberAvailableResponseData> => {
  try {
    const adaptedParams = adaptCheckParams(params);
    const response = await communicationService.checkAreaNumberAvailable(adaptedParams);
    
    // Adapt response format for backward compatibility
    return {
      statusCode: response.statusCode,
      message: response.message,
      data: response.data.available, // Return only available codes for backward compatibility
      timestamp: response.timestamp,
      timeTakenMs: response.timeTakenMs,
      path: response.path,
    };
  } catch (error) {
    // Enhanced service already handles errors and toasts
    throw error;
  }
};

// Start area number checkout
export const startAreaNumberCheckout = async (
  params: StartAreaNumberCheckoutParams
): Promise<StartAreaNumberCheckoutResponse> => {
  try {
    const adaptedParams = adaptCheckoutParams(params);
    const response = await communicationService.startAreaNumberCheckout(adaptedParams);
    
    // Adapt response format for backward compatibility
    return {
      statusCode: response.statusCode,
      message: response.message,
      data: {
        url: response.data.url,
        transactionId: response.data.transactionId,
        dryRun: response.data.dryRun,
      },
      url: response.data.url,
      transactionId: response.data.transactionId,
      dryRun: response.data.dryRun,
      timestamp: response.timestamp,
      timeTakenMs: response.timeTakenMs,
      path: response.path,
    };
  } catch (error) {
    // Enhanced service already handles errors and toasts
    throw error;
  }
};

// Get area code cost
export const getAreaCodeCost = async (codes: string, stateName?: string): Promise<AreaCodeCostResponse> => {
  try {
    const response = await communicationService.getAreaCodeCost(codes, stateName);
    
    // Adapt response format for backward compatibility
    return {
      areaCodes: response.data.areaCodes,
      cost: response.data.cost,
      currency: response.data.currency,
    };
  } catch (error) {
    // Enhanced service already handles errors and toasts
    throw error;
  }
};

export const subscribeToAreaCodes = async (params: SubscribeToAreaCodesParams): Promise<SubscribeToAreaCodesResponse> => {
  try {
    const adaptedParams = adaptSubscribeParams(params);
    const response = await communicationService.subscribeToAreaCodes(adaptedParams);
    
    // Adapt response format for backward compatibility
    return {
      statusCode: response.statusCode,
      message: response.message,
      data: {
        provisionedNumbers: response.data.provisionedNumbers,
      },
      timestamp: response.timestamp,
      timeTakenMs: response.timeTakenMs,
      path: response.path,
    };
  } catch (error) {
    // Enhanced service already handles errors and toasts
    throw error;
  }
};

